import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScanText, ShieldCheck, PlusCircle, RotateCcw } from "lucide-react";
import { PlantUpload } from "@/components/verification/PlantUpload";
import { EntryTable } from "@/components/verification/EntryRow";
import { PlantResults } from "@/components/verification/ResultsView";
import { WebsiteEditDialog } from "@/components/verification/WebsiteEditDialog";
import { SavedReports, AuditHistory } from "@/components/verification/History";
import { QuestionsPanel } from "@/components/verification/QuestionsPanel";
import { PagesPanel } from "@/components/verification/PagesPanel";
import { compressImage, cropLines, newEntry } from "@/components/verification/imageUtils";

const DRAFT = "sfx_book_verification_draft";
const thisMonth = () => new Date().toISOString().slice(0, 7);
const loadDraft = () => { try { return JSON.parse(localStorage.getItem(DRAFT)) || null; } catch { return null; } };
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function readOnePage(month, p, file) {
  const image_base64 = await compressImage(file);
  const { data: job } = await api.post("/verification/read-page", { month, plant: p.plant, page: p.page, image_base64 });
  for (let t = 0; t < 120; t++) {
    await sleep(4000);
    const { data } = await api.get(`/verification/read-page/${job.job_id}`);
    if (data.status === "failed") throw new Error(data.error);
    if (data.status === "done") {
      const entries = await cropLines(image_base64, data.result.entries, data.result.rotation);
      return { ...data.result, entries: entries.map(({ box_2d, ...e }) => e) };
    }
  }
  throw new Error("Reading took too long — please retry this page");
}

export default function BookVerification() {
  const draft = useRef(loadDraft()).current;
  const [tab, setTab] = useState("new");
  const [plants, setPlants] = useState([]);
  const [month, setMonth] = useState(draft?.month || thisMonth());
  const [uploads, setUploads] = useState({});
  const [pages, setPages] = useState(draft?.pages || []);
  const [entries, setEntries] = useState(draft?.entries || []);
  const [result, setResult] = useState(null);
  const [editIds, setEditIds] = useState(null);
  const [webSale, setWebSale] = useState(null);
  const [approve, setApprove] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const files = useRef({});
  const read = [...new Set(pages.map((p) => p.plant))];
  const busy = pages.some((p) => ["pending", "reading"].includes(p.status));

  useEffect(() => { api.get("/plants").then((r) => setPlants(r.data.map((p) => p.name))); }, []);
  useEffect(() => {
    if (!pages.length) return;
    try { localStorage.setItem(DRAFT, JSON.stringify({ month, pages, entries })); }
    catch { toast.warning("Draft too large to keep after a page reload — finish this review before closing."); }
  }, [month, pages, entries]);

  const compare = async () => {
    try { setResult((await api.post("/verification/compare", { month, plants: read, entries })).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => {
    if (!read.length) { setResult(null); return undefined; }
    const t = setTimeout(compare, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, read.join("|"), month]);

  const setPage = (key, upd) => setPages((l) => l.map((p) => (p.key === key ? { ...p, ...upd } : p)));

  const runPage = async (p) => {
    setPage(p.key, { status: "reading", error: "" });
    try {
      const res = await readOnePage(month, p, files.current[p.key]);
      setEntries((l) => [...l.filter((e) => !(e.plant === p.plant && e.page === p.page)), ...res.entries]);
      setPage(p.key, { status: "read", count: res.entries.length, issues: res.page_issues });
      delete files.current[p.key];
    } catch (e) {
      setPage(p.key, { status: "failed", error: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  const startReading = async () => {
    const list = plants.flatMap((pl) => (uploads[pl] || []).map((f, i) => ({ key: `${pl}-${i + 1}`.replace(/\s+/g, ""), plant: pl, page: i + 1, name: f.name, status: "pending", file: f })));
    if (!list.length) { toast.error("Add at least one notebook photo"); return; }
    list.forEach((p) => { files.current[p.key] = p.file; });
    setPages(list.map(({ file, ...p }) => p));
    setUploads({});
    const queue = [...list];
    const worker = async () => { while (queue.length) await runPage(queue.shift()); };
    await Promise.all([worker(), worker()]);
    toast.success("All pages processed — please answer the questions and review the results");
  };

  const retry = (p, file) => { if (file) files.current[p.key] = file; runPage(p); };
  const manual = (p) => setPage(p.key, { status: "manual", error: "" });
  const update = (e) => setEntries((list) => list.map((x) => (x.id === e.id ? e : x)));
  const remove = (id) => setEntries((list) => list.filter((x) => x.id !== id));
  const add = (plant, extra) => { const e = newEntry(plant, extra); setEntries((l) => [...l, e]); return e; };
  const reset = () => { localStorage.removeItem(DRAFT); files.current = {}; setPages([]); setEntries([]); setUploads({}); setRemarks(""); };

  const saveReport = async () => {
    setSaving(true);
    try {
      await api.post("/verification/reports", { month, plants: read, entries: entries.map(({ crop, ...e }) => e), pages, remarks });
      toast.success("Verification report approved and saved");
      reset(); setApprove(false); setTab("saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const res = result?.resolution || {};
  const customers = result?.customers || [];
  const badPages = pages.filter((p) => !["read", "manual"].includes(p.status)).length;
  const blocked = busy || !result || result.open_questions > 0 || badPages > 0;

  return (
    <div data-testid="book-verification-page" className="space-y-5">
      <div>
        <h1 className="font-chivo font-black text-3xl text-slate-900 flex items-center gap-2"><ScanText className="h-7 w-7 text-sky-600" /> Book Verification</h1>
        <p className="text-slate-500 mt-1">Compare handwritten notebook pages with website sales. Read-only — website records change only when you confirm an edit.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto max-w-full">
          <TabsTrigger value="new" data-testid="tab-new-verification">New Verification</TabsTrigger>
          <TabsTrigger value="saved" data-testid="tab-saved-reports">Saved Reports</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit-history">Website Edit History</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="space-y-5 mt-4">
          {!pages.length ? (
            <>
              <div className="max-w-xs"><Label>Month</Label><Input type="month" data-testid="verification-month-input" value={month} onChange={(e) => setMonth(e.target.value)} /></div>
              <div className="grid md:grid-cols-2 gap-4">
                {plants.map((p) => <PlantUpload key={p} plant={p} files={uploads[p] || []} onChange={(f) => setUploads({ ...uploads, [p]: f })} />)}
              </div>
              <Button data-testid="start-reading-button" className="bg-sky-600 hover:bg-sky-700 h-11" onClick={startReading}><ScanText className="h-4 w-4 mr-2" />Read Photos & Compare</Button>
              <p className="text-xs text-slate-500">Each page is read twice by the AI (about 1–2 minutes per page). Photos are never stored.</p>
            </>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2 justify-between bg-sky-50 border border-sky-200 rounded-xl p-3">
                <div className="text-sm text-sky-900">Reviewing <b>{month}</b> · {read.join(" & ")} · {entries.length} lines</div>
                <div className="flex gap-2">
                  <Button variant="outline" data-testid="discard-draft-button" disabled={busy} onClick={reset}><RotateCcw className="h-4 w-4 mr-1" />Start Over</Button>
                  <Button data-testid="open-approve-button" disabled={blocked} className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setApprove(true)}><ShieldCheck className="h-4 w-4 mr-1" />Approve & Save Report</Button>
                </div>
              </div>
              {result && blocked && !busy && (
                <p data-testid="approve-blocked-reason" className="text-sm text-amber-800">
                  Approval locked: {result.open_questions} unanswered question(s){badPages ? `, ${badPages} page(s) not read` : ""}.
                </p>
              )}
              <PagesPanel pages={pages} files={files.current} onRetry={retry} onManual={manual} />
              {result && <QuestionsPanel entries={entries} resolution={res} customers={customers} onUpdate={update} onDelete={remove} />}
              {result && read.map((p) => (
                <div key={p} className="space-y-3">
                  <PlantResults plant={p} data={result.plants[p]} onEditBook={(r) => setEditIds(r.book_entry_ids)} onEditWeb={setWebSale}
                    onAddBook={(r) => setEditIds([add(p, { date: r.date, customer_read: r.customer, matched_customer: r.customer }).id])} />
                  <details data-testid={`all-entries-${p.replace(/\s+/g, "-").toLowerCase()}`} className="bg-white border rounded-xl p-4">
                    <summary className="cursor-pointer font-semibold text-sm">All book lines read for {p} ({entries.filter((e) => e.plant === p).length}) — edit any reading</summary>
                    <div className="mt-3 space-y-2">
                      <EntryTable entries={entries.filter((e) => e.plant === p)} resolution={res} customers={customers} onUpdate={update} onDelete={remove} />
                      <Button size="sm" variant="outline" data-testid={`add-entry-${p.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => add(p, { date: `${month}-01` })}><PlusCircle className="h-4 w-4 mr-1" />Add book line</Button>
                    </div>
                  </details>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="saved" className="mt-4">{tab === "saved" && <SavedReports />}</TabsContent>
        <TabsContent value="audit" className="mt-4">{tab === "audit" && <AuditHistory />}</TabsContent>
      </Tabs>

      <Dialog open={!!editIds} onOpenChange={(o) => !o && setEditIds(null)}>
        <DialogContent data-testid="edit-reading-dialog" className="max-w-4xl">
          <DialogHeader><DialogTitle>Correct AI Reading</DialogTitle>
            <DialogDescription>Fixes only the temporary book reading. Totals and comparison update automatically.</DialogDescription></DialogHeader>
          <EntryTable entries={entries.filter((e) => editIds?.includes(e.id))} resolution={res} customers={customers} onUpdate={update} onDelete={remove} />
          <DialogFooter><Button data-testid="edit-reading-done" onClick={() => setEditIds(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <WebsiteEditDialog sale={webSale} month={month} onClose={() => setWebSale(null)} onSaved={() => { setWebSale(null); compare(); }} />

      <Dialog open={approve} onOpenChange={setApprove}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader><DialogTitle>Approve & Save Report</DialogTitle>
            <DialogDescription>All questions are answered and every page is processed. Mismatches and missing entries will be saved in the report.</DialogDescription></DialogHeader>
          <div><Label>Remarks (optional)</Label><Textarea data-testid="approve-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprove(false)}>Cancel</Button>
            <Button data-testid="approve-save-button" className="bg-emerald-600 hover:bg-emerald-700" disabled={saving} onClick={saveReport}>{saving ? "Saving…" : "I Approve — Save Report"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
